import React from "react";
import { useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { BatchesContext } from "../common/BatchesContext";
import { UserContext } from "../common/UserContext";
import { Application, Process, makeRequest } from "../common/api";
import { FixedContainer, PageBody } from "../common/components/PageBody";
import { ApplyForm } from "../common/features/ApplyForm";
import { LoginForm } from "../common/features/LoginForm";
import { LoadingPage, Markdown, Required } from "../common/features/Utils";

const GlobalStyle = createGlobalStyle`
  h4 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #191919;
    margin: 0 0 12px 0;
  }
  p {
    font-size: 1.4rem;
  }
`;

// Styled components for course selection
const CourseCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  margin-top: 10px;
  width: 100%;

  @media (min-width: 769px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    max-width: 1024px;
    margin: 20px auto;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const CourseCard = styled.label<{ selected: boolean }>`
  display: block;
  padding: 20px;
  border: 2px solid ${(props) => (props.selected ? "#9051a8" : "#e0e0e0")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.selected ? "#f8f9ff" : "white")};
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: #9051a8;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 12px;
    transform: none;

    &:hover {
      transform: none;
      box-shadow: none;
      border-color: #9051a8;
    }
  }

  input[type="radio"] {
    display: none;
  }
`;

const CourseTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 16px;
    gap: 8px;
  }
`;

const CourseDescription = styled.div<{ isExpanded: boolean }>`
  font-size: 16px;
  line-height: 1.7;
  color: #555;
  font-weight: 400;
  overflow: hidden;
  max-height: none;
  position: relative;
  transition: max-height 0.3s ease-out;

  p {
    margin: 0;
    font-weight: inherit;
    text-align: left;
  }

  strong, b {
    font-weight: 600;
  }

  @media (max-width: 768px) {
    text-align: left;
    max-height: ${(props) => (props.isExpanded ? "none" : "3em")};
    p {
      
    }
  }
`;

const RadioIndicator = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => (props.selected ? "#9051a8" : "#ccc")};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => (props.selected ? "#9051a8" : "transparent")};
  }
`;

const ToggleButtonContainer = styled.div`
  display: none;
  justify-content: flex-end;
  margin-top: 4px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const ToggleButton = styled.button`
  background: none !important;
  border: none !important;
  padding: 0;
  color: #9051a8 !important;
  cursor: pointer;
  font-size: 13px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  transition: opacity 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  * {
    font-weight: 400 !important;
    font-family: inherit;
  }

  &:hover,
  &:focus,
  &:active {
    opacity: 0.8;
    text-decoration: underline;
    outline: none;
  }
`;

// Add new styled component for the message box
const ExistingApplicationMessage = styled.div`
  padding: 20px;
  background-color: #f8f9ff;
  border: 1px solid #9051a8;
  border-radius: 8px;
  margin: 20px 0;
  text-align: center;

  p {
    color: #555;
    margin: 0 0 20px 0;
    font-size: 16px;
  }
`;

const ViewApplicationButton = styled.button`
  background-color: #9051a8;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #7b4491;
  }
`;

interface Props {}
export const ApplyPage = (props: Props) => {
  const userContext = React.useContext(UserContext);
  const batches = React.useContext(BatchesContext)?.batches;
  const location = useLocation();
  const batchIdFromUrl = new URLSearchParams(location.search).get("batchId");
  const [selectedBatchId, setSelectedBatchId] = React.useState(batchIdFromUrl || "");
  const [duplicateEmail, setDuplicateEmail] = React.useState("");
  const [existingApplications, setExistingApplications] = React.useState<Application[] | null>(null);
  const [isCheckingApplication, setIsCheckingApplication] = React.useState(false);
  const [redirectMessage, setRedirectMessage] = React.useState("");
  const [expandedCards, setExpandedCards] = React.useState<Set<string>>(new Set());
  const [existingApplication, setExistingApplication] = React.useState<Application | null>(null);

  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [showLogin, setShowLogin] = React.useState(false);
  const [application, setApplication] = React.useState<Application | null>(null);
  const [submittedProcess, setSubmittedProcess] = React.useState<Process | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const formRef = React.useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = React.useState(false);

  // Add effect to handle initial batchId from URL
  React.useEffect(() => {
    if (batchIdFromUrl && batches) {
      const isValidBatchId = batches.some(batch => batch._id === batchIdFromUrl && batch.status === "Open");
      if (isValidBatchId) {
        setSelectedBatchId(batchIdFromUrl);

        // Check for existing application
        if (existingApplications) {
          const existingApp = existingApplications.find((app) => app.batchId === batchIdFromUrl);
          if (existingApp) {
            const batch = batches.find((b) => b._id === batchIdFromUrl);
            setExistingApplication(existingApp);
            setRedirectMessage(`You have already applied for ${batch?.name}.`);
            setShowForm(false);
            return;
          }
        }

        setShowForm(true);
        setShouldScroll(true);
        setExistingApplication(null);
        setRedirectMessage("");
      }
    }
  }, [batchIdFromUrl, batches, existingApplications]);

  // Add effect to auto-select single batch
  React.useEffect(() => {
    if (batches && !batchIdFromUrl && existingApplications !== null) {
      const activeBatches = batches.filter((b) => b.status === "Open");
      if (activeBatches.length === 1) {
        const singleBatch = activeBatches[0];
        setSelectedBatchId(singleBatch._id);
        handleBatchSelect(singleBatch._id);
      }
    }
  }, [batches, batchIdFromUrl, existingApplications]);

  React.useEffect(() => {
    if (shouldScroll && showForm) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setShouldScroll(false);
      }, 500);
    }
  }, [shouldScroll, showForm]);

  // Get the current batch name
  const currentBatch = batches?.find((b) => b._id === (selectedBatchId || batchIdFromUrl));

  // Fetch existing applications when user is logged in
  React.useEffect(() => {
    if (userContext?.user) {
      setIsCheckingApplication(true);
      makeRequest("/api/applications", "GET").then((apps: Application[]) => {
        setExistingApplications(apps);

        // Check for existing application if batchId is in URL
        if (batchIdFromUrl && apps.length > 0) {
          const existingApp = apps.find((app) => app.batchId === batchIdFromUrl && app.status === "Open");
          if (existingApp) {
            setExistingApplication(existingApp);
            const batch = batches?.find((b) => b._id === batchIdFromUrl);
            setRedirectMessage(`You have already applied for ${batch?.name}.`);
          }
        }
        setIsCheckingApplication(false);
      });
    }
  }, [userContext, batchIdFromUrl, batches]);

  const handleBatchSelect = (batchId: string) => {
    setSelectedBatchId(batchId);

    if (!userContext?.user || !existingApplications) {
      setShowForm(true);
      setShouldScroll(true);
      setExistingApplication(null);
      setRedirectMessage("");
      return;
    }

    const existingApp = existingApplications.find((app) => app.batchId === batchId);
    if (existingApp) {
      const batch = batches?.find((b) => b._id === batchId);
      setExistingApplication(existingApp);
      setRedirectMessage(`You have already applied for ${batch?.name}.`);
      setShowForm(false);
      return;
    }

    setShowForm(true);
    setShouldScroll(true);
    setExistingApplication(null);
    setRedirectMessage("");
  };

  const toggleDescription = (batchId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  if (!batches) {
    return <LoadingPage />;
  }

  // Verify that the batchId from URL is valid
  const isValidBatchId = batchIdFromUrl && batches.some((batch) => batch._id === batchIdFromUrl && batch.status === "Open");
  const activeBatches = batches.filter((b) => b.status === "Open");

  if (activeBatches.length === 0) {
    return (
      <PageBody>
        <FixedContainer>
          Sorry, this school is not currently accepting applications for any of our programs.
          <br />
          <br />
          We value all potential students and encourage you to check back soon, or follow us on Facebook
          to find out as soon as we announce our next schedule.
        </FixedContainer>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <GlobalStyle />
      <FixedContainer>
        {isCheckingApplication ? (
          <div
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              color: "#666",
            }}
          >
            Checking for existing applications...
          </div>
        ) : showLogin ? (
          <div>
            <p>
              You've already applied to {currentBatch?.name || "this course"}. Please log in to view
              your application.
            </p>
            <div style={{ width: "300px" }}>
              <LoginForm
                onComplete={(user) => {
                  window.location.href = "/";
                }}
                email={duplicateEmail}
              />
            </div>
          </div>
        ) : !isSubmitted ? (
          <>
            {/* Course Selection - Only show if no valid batchId in URL */}
            {!isValidBatchId && (
              <>
                <h4>Future-proof. Upskill in Tech & AI!</h4>
                <p>
                  Choose the program that fits your goals and enroll in just 2 minutes:
                  <Required />
                </p>
                <CourseCards>
                  {activeBatches.map((batch) => (
                    <CourseCard
                      key={batch._id}
                      selected={selectedBatchId === batch._id}
                      onClick={() => handleBatchSelect(batch._id)}
                    >
                      <input
                        type="radio"
                        name="course"
                        value={batch._id}
                        checked={selectedBatchId === batch._id}
                        onChange={(e) => handleBatchSelect(e.target.value)}
                      />
                      <CourseTitle>
                        <RadioIndicator selected={selectedBatchId === batch._id} />
                        {batch.name}
                      </CourseTitle>
                      <CourseDescription isExpanded={expandedCards.has(batch._id)}>
                        <Markdown content={batch.description || ""} />
                      </CourseDescription>
                      <ToggleButtonContainer>
                        <ToggleButton onClick={(e) => toggleDescription(batch._id, e)}>
                          <span>{expandedCards.has(batch._id) ? "Show Less" : "Show More"}</span>
                          <span style={{ fontSize: "8px" }}>
                            {expandedCards.has(batch._id) ? "▲" : "▼"}
                          </span>
                        </ToggleButton>
                      </ToggleButtonContainer>
                    </CourseCard>
                  ))}
                </CourseCards>
              </>
            )}

            {/* Show existing application message below course list */}
            {redirectMessage && existingApplication && (
              <ExistingApplicationMessage>
                <h4>{redirectMessage}</h4>
                <p>You can view or update your existing application.</p>
                <ViewApplicationButton
                  onClick={() => window.location.href = `/application/${existingApplication._id}`}
                >
                  View My Enrollment Application
                </ViewApplicationButton>
              </ExistingApplicationMessage>
            )}

            <div ref={formRef} style={{ marginTop: "20px", scrollMarginTop: "50px" }}>
              {showForm && (
                <ApplyForm
                  batchId={selectedBatchId}
                  onSubmitted={(app: Application, process: Process) => {
                    setApplication(app);
                    setSubmittedProcess(process);
                    setIsSubmitted(true);
                    if (userContext?.user) {
                      window.location.href = `/application/${app._id}`;
                    }
                  }}
                  onDuplicateApplication={(email) => {
                    setSelectedBatchId(batchIdFromUrl || "");
                    setDuplicateEmail(email);
                    setShowLogin(true);
                  }}
                />
              )}
              {" "}
            </div>
          </>
        ) : null}

        {isSubmitted && !userContext?.user && submittedProcess?.requestSignup && (
            <>
              <p>
              Thank you, {application?.firstName}. Add a password so you can manage and update your
              application.
              </p>

              <div style={{ width: "300px" }}>
                <LoginForm
                  isSignupOnly={true}
                  onComplete={async (user) => {
                    if (application && user) {
                      application.userId = user._id;
                    await makeRequest(`/api/applications/${application._id}`, "PUT", application);
                      window.location.href = `/application/${application._id}`;
                    }
                  }}
                  email={application?.email}
                />
              </div>
            </>
          )}
        {isSubmitted && !userContext?.user && !submittedProcess?.requestSignup && (
            <div>Thank you for providing your information!</div>
          )}
      </FixedContainer>
    </PageBody>
  );
};
